/**
 * Session metrics
 * Pure functions to compute dashboard KPIs and analytics breakdowns from session data.
 */

import { BRRSession } from './types';
import { SessionStatus } from './sessionStatus';

/**
 * Dashboard KPI statistics computed from session data
 */
export interface DashboardStats {
  totalInPipeline: number;
  avgProcessingTimeDays: number;
  completedCount: number;
  pendingActions: number;
}

/**
 * Compute dashboard KPI statistics from a list of sessions.
 * Pure function — no API calls.
 * @param sessions - All sessions from the session store
 * @param columns - Column name mapping from sessionClient.columns
 */
export function computeDashboardStats(
  sessions: BRRSession[],
  columns: { status: string; startedat: string; completedat: string; details?: string }
): DashboardStats {
  let totalInPipeline = 0;
  let completedCount = 0;
  let pendingActions = 0;
  let totalProcessingMs = 0;
  let processedCount = 0;

  for (const s of sessions) {
    const status = s[columns.status];
    if (status === SessionStatus.Completed) {
      completedCount++;
      let processStart = s[columns.startedat];
      if (columns.details) {
        try {
          const details = s[columns.details];
          if (details) {
            const parsed = JSON.parse(details);
            if (parsed?.inProgressAt) processStart = parsed.inProgressAt;
            else if (parsed?.docCompletenessAt) processStart = parsed.docCompletenessAt;
          }
        } catch { /* ignore */ }
      }
      const completed = s[columns.completedat];
      if (processStart && completed) {
        const ms = new Date(completed).getTime() - new Date(processStart).getTime();
        if (ms > 0) {
          totalProcessingMs += ms;
          processedCount++;
        }
      }
    } else if (
      status === SessionStatus.Draft ||
      status === SessionStatus.InProgress ||
      status === SessionStatus.Submitted
    ) {
      totalInPipeline++;
      if (status === SessionStatus.Submitted) {
        pendingActions++;
      }
    }
  }

  const avgProcessingTimeDays = processedCount > 0
    ? totalProcessingMs / processedCount / (1000 * 60 * 60 * 24)
    : 0;

  return { totalInPipeline, avgProcessingTimeDays, completedCount, pendingActions };
}

export interface AnalyticsBreakdownEntry {
  label: string;
  avgDays: number;
  count: number;
}

export interface AnalyticsBreakdown {
  avgTimeByReviewer: AnalyticsBreakdownEntry[];
  avgTimeByPriority: AnalyticsBreakdownEntry[];
  avgTimeByBatchType: AnalyticsBreakdownEntry[];
}

export function computeAnalyticsBreakdown(
  sessions: BRRSession[],
  columns: { status: string; startedat: string; completedat: string; useremail: string; details?: string }
): AnalyticsBreakdown {
  const reviewerMap = new Map<string, { totalMs: number; count: number }>();
  const priorityMap = new Map<string, { totalMs: number; count: number }>();
  const batchTypeMap = new Map<string, { totalMs: number; count: number }>();

  for (const s of sessions) {
    if (s[columns.status] !== SessionStatus.Completed) continue;

    let processStart = s[columns.startedat];
    let priority = 'medium';
    let batchType = 'unknown';

    if (columns.details) {
      try {
        const details = s[columns.details];
        if (details) {
          const parsed = JSON.parse(details);
          if (parsed?.inProgressAt) processStart = parsed.inProgressAt;
          else if (parsed?.docCompletenessAt) processStart = parsed.docCompletenessAt;
          priority = parsed?.priority === 'normal' ? 'medium' : (parsed?.priority || 'medium');
          batchType = parsed?.batchType || 'unknown';
        }
      } catch { /* ignore */ }
    }

    const completed = s[columns.completedat];
    if (!processStart || !completed) continue;
    const ms = new Date(completed).getTime() - new Date(processStart).getTime();
    if (ms <= 0) continue;

    const reviewer = s[columns.useremail] || 'Unknown';

    const rev = reviewerMap.get(reviewer) ?? { totalMs: 0, count: 0 };
    rev.totalMs += ms; rev.count++;
    reviewerMap.set(reviewer, rev);

    const pri = priorityMap.get(priority) ?? { totalMs: 0, count: 0 };
    pri.totalMs += ms; pri.count++;
    priorityMap.set(priority, pri);

    const bt = batchTypeMap.get(batchType) ?? { totalMs: 0, count: 0 };
    bt.totalMs += ms; bt.count++;
    batchTypeMap.set(batchType, bt);
  }

  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const toResult = (map: Map<string, { totalMs: number; count: number }>): AnalyticsBreakdownEntry[] =>
    [...map.entries()]
      .map(([label, { totalMs, count }]) => ({ label, avgDays: totalMs / count / MS_PER_DAY, count }))
      .sort((a, b) => a.avgDays - b.avgDays);

  return {
    avgTimeByReviewer: toResult(reviewerMap),
    avgTimeByPriority: toResult(priorityMap),
    avgTimeByBatchType: toResult(batchTypeMap),
  };
}
